// Same rules as web/src/lib/dayServiceOrder.ts; keep the two in sync.
/** A day plan service kind that can be interleaved with the other kind. */
export type DayServiceKind = "transport" | "activity";

/** Points at one service in a day's transports or activities list. */
export type DayServiceRef = { kind: DayServiceKind; index: number };

type Orderable = { dayOrder?: number | null } | null | undefined;

const explicitOrder = (item: Orderable): number | null => {
  if (item?.dayOrder === null || item?.dayOrder === undefined) return null;
  const order = Number(item.dayOrder);
  return Number.isFinite(order) ? order : null;
};

/** True when any service in the list has an explicit position in its day (`dayOrder`). */
export const hasDayOrder = (items: Orderable[]): boolean => items.some((item) => explicitOrder(item) !== null);

/**
 * Lists a day's transports and activities in display order.
 * Services with a `dayOrder` come first, by that order; services without one keep the legacy
 * order after them (transports, then activities, each in list order), so untouched days look as before.
 */
export const orderDayServices = (transports: Orderable[], activities: Orderable[]): DayServiceRef[] =>
  [
    ...transports.map((item, index) => ({ kind: "transport" as const, index, order: explicitOrder(item), legacy: index })),
    ...activities.map((item, index) => ({
      kind: "activity" as const,
      index,
      order: explicitOrder(item),
      legacy: transports.length + index,
    })),
  ]
    .sort((left, right) => {
      if ((left.order === null) !== (right.order === null)) return left.order === null ? 1 : -1;
      if (left.order !== null && right.order !== null && left.order !== right.order) return left.order - right.order;
      return left.legacy - right.legacy;
    })
    .map(({ kind, index }) => ({ kind, index }));

/** The day orders an ↑/↓ move produces: the item's new day and each affected day's new service order. */
export type DayServiceMovePlan<T> = { toDay: number; changes: Array<{ day: number; order: T[] }> };

/**
 * Resolves an ↑/↓ move in a day-by-day plan (the Quotes daily plan rules), across both service kinds:
 * middle items swap with their neighbour, ↑ on a day's first item moves it to the end of the previous day,
 * and ↓ on a day's last item moves it to the start of the next day.
 * Returns null for ↑ on the first item of Day 1 and ↓ on the last item of the final day.
 * @param orderByDay each day's services in display order
 */
export const planDayServiceMove = <T>(
  orderByDay: T[][],
  day: number,
  position: number,
  direction: "up" | "down",
): DayServiceMovePlan<T> | null => {
  const current = orderByDay[day] || [];
  if (position < 0 || position >= current.length) return null;
  const item = current[position];

  if (direction === "up" && position > 0) {
    const order = [...current];
    [order[position - 1], order[position]] = [order[position], order[position - 1]];
    return { toDay: day, changes: [{ day, order }] };
  }
  if (direction === "down" && position < current.length - 1) {
    const order = [...current];
    [order[position + 1], order[position]] = [order[position], order[position + 1]];
    return { toDay: day, changes: [{ day, order }] };
  }

  const toDay = direction === "up" ? day - 1 : day + 1;
  if (toDay < 0 || toDay >= orderByDay.length) return null;
  const remaining = current.filter((_entry, index) => index !== position);
  const target = orderByDay[toDay] || [];
  const order = direction === "up" ? [...target, item] : [item, ...target];
  return { toDay, changes: [{ day, order: remaining }, { day: toDay, order }] };
};
